// @client/utils/deep-link-helpers.ts
// Deep-Link System für Profile Manager Filter-Übergabe via URL

import type { ProfileFilters } from '@shared/types';

/**
 * Build URL query string from current filters
 * 
 * @param filters - Current filter state
 * @returns URL path with query string (e.g., "/profile-manager?listId=5&scoreRange=high")
 * 
 * @example
 * const url = buildDeepLink({ listId: 5, scoreRange: 'high', search: 'Munich' });
 * // Returns: "/profile-manager?listId=5&scoreRange=high&search=Munich"
 */
export function buildDeepLink(filters: ProfileFilters): string {
  const params = new URLSearchParams();
  
  // Text Search
  if (filters.search && filters.search.trim()) {
    params.set('search', filters.search.trim());
  }
  
  // List Filter
  if (filters.listId && filters.listId !== 'all') {
    params.set('listId', filters.listId.toString());
  }
  
  // Category Filter (Multi-Select)
  if (filters.categoryIds && filters.categoryIds.length > 0) {
    params.set('categoryIds', filters.categoryIds.join(','));
  }
  
  // Score Range
  if (filters.scoreRange && filters.scoreRange !== 'all') {
    params.set('scoreRange', filters.scoreRange);
  }
  
  // Created By User
  if (filters.createdBy) {
    params.set('createdBy', filters.createdBy.toString());
  }
  
  // After Date Filter
  if (filters.afterField && filters.afterDate) {
    params.set('afterField', filters.afterField);
    params.set('afterDate', filters.afterDate);
  }
  
  // Before Date Filter
  if (filters.beforeField && filters.beforeDate) {
    params.set('beforeField', filters.beforeField);
    params.set('beforeDate', filters.beforeDate);
  }
  
  // Boolean Markers
  if (filters.isExported !== undefined) {
    params.set('isExported', filters.isExported.toString());
  }
  if (filters.isActive !== undefined) {
    params.set('isActive', filters.isActive.toString());
  }
  if (filters.isBlocked !== undefined) {
    params.set('isBlocked', filters.isBlocked.toString());
  }
  if (filters.isRegistered !== undefined) {
    params.set('isRegistered', filters.isRegistered.toString());
  }
  if (filters.hasCategories !== undefined) {
    params.set('hasCategories', filters.hasCategories.toString());
  }
  if (filters.hasLists !== undefined) {
    params.set('hasLists', filters.hasLists.toString());
  }
  if (filters.hasEmail !== undefined) params.set('hasEmail', filters.hasEmail.toString());
  
  const queryString = params.toString();
  return queryString ? `/profile-manager?${queryString}` : '/profile-manager';
}

/**
 * Parse URL query string to filter object
 * 
 * @param searchParams - URLSearchParams from React Router
 * @returns Partial filter object (only fields present in URL)
 * 
 * @example
 * const filters = parseDeepLink(new URLSearchParams('?listId=5&scoreRange=high'));
 * // Returns: { listId: 5, scoreRange: 'high' }
 */
export function parseDeepLink(searchParams: URLSearchParams): Partial<ProfileFilters> {
  const filters: Partial<ProfileFilters> = {};
  
  // Text Search
  const search = searchParams.get('search');
  if (search && search.trim()) {
    filters.search = search.trim();
  }
  
  // List Filter
  const listId = searchParams.get('listId');
  if (listId) {
    const parsed = parseInt(listId);
    if (!isNaN(parsed)) {
      filters.listId = parsed;
    }
  }
  
  // Category Filter (Multi-Select)
  const categoryIds = searchParams.get('categoryIds');
  if (categoryIds) {
    const ids = categoryIds.split(',')
      .map(id => parseInt(id.trim()))
      .filter(id => !isNaN(id));
    
    if (ids.length > 0) {
      filters.categoryIds = ids;
    }
  }
  
  // Score Range
  const scoreRange = searchParams.get('scoreRange');
  if (scoreRange) {
    filters.scoreRange = scoreRange;
  }
  
  // Created By User
  const createdBy = searchParams.get('createdBy');
  if (createdBy) {
    const parsed = parseInt(createdBy);
    if (!isNaN(parsed)) {
      filters.createdBy = parsed;
    }
  }
  
  // After Date Filter
  const afterField = searchParams.get('afterField');
  const afterDate = searchParams.get('afterDate');
  if (afterField && ( afterField == 'createdAt' || afterField == 'modifiedAt' || afterField == 'exportedAt') && afterDate) {
    filters.afterField = afterField;
    filters.afterDate = afterDate;
  }
  
  // Before Date Filter
  const beforeField = searchParams.get('beforeField');
  const beforeDate = searchParams.get('beforeDate');
  if (beforeField && ( beforeField == 'createdAt' || beforeField == 'modifiedAt' || beforeField == 'exportedAt') && beforeDate) {
    filters.beforeField = beforeField;
    filters.beforeDate = beforeDate;
  }
  
  // Boolean Markers
  const isExported = searchParams.get('isExported');
  if (isExported === 'true' || isExported === 'false') {
    filters.isExported = isExported === 'true';
  }
  
  const isActive = searchParams.get('isActive');
  if (isActive === 'true' || isActive === 'false') {
    filters.isActive = isActive === 'true';
  }
  
  const isBlocked = searchParams.get('isBlocked');
  if (isBlocked === 'true' || isBlocked === 'false') {
    filters.isBlocked = isBlocked === 'true';
  }
  
  const isRegistered = searchParams.get('isRegistered');
  if (isRegistered === 'true' || isRegistered === 'false') {
    filters.isRegistered = isRegistered === 'true';
  }
  
  const hasCategories = searchParams.get('hasCategories');
  if (hasCategories === 'true' || hasCategories === 'false') {
    filters.hasCategories = hasCategories === 'true';
  }
  
  const hasLists = searchParams.get('hasLists');
  if (hasLists === 'true' || hasLists === 'false') {
    filters.hasLists = hasLists === 'true';
  }

  const hasEmail = searchParams.get('hasEmail');
  if (hasEmail === 'true' || hasEmail === 'false') {
    filters.hasEmail = hasEmail === 'true';
  }
  
  return filters;
}

/**
 * Build full absolute URL for sharing
 * 
 * @param filters - Current filter state
 * @returns Full URL including origin (e.g., "https://example.com/profile-manager?...")
 * 
 * @example
 * const url = buildShareableLink({ listId: 5 });
 * // Returns: "https://spaces.kongressmedia.de/profile-manager?listId=5"
 */
export function buildShareableLink(filters: ProfileFilters): string {
  const relativePath = buildDeepLink(filters);
  return `${window.location.origin}${relativePath}`;
}

/**
 * Quick helper: Create deep link for specific list
 * 
 * @param listId - List ID
 * @returns URL path with listId parameter
 * 
 * @example
 * <Button onClick={() => navigate(createListDeepLink(5))}>
 *   View Profiles
 * </Button>
 */
export function createListDeepLink(listId: number): string {
  return `/profile-manager?listId=${listId}`;
}

/**
 * Quick helper: Create deep link for specific category
 * 
 * @param categoryId - Category ID (single or array)
 * @returns URL path with categoryIds parameter
 * 
 * @example
 * <Button onClick={() => navigate(createCategoryDeepLink(3))}>
 *   View Profiles
 * </Button>
 */
export function createCategoryDeepLink(categoryId: number | number[]): string {
  const ids = Array.isArray(categoryId) ? categoryId : [categoryId];
  return `/profile-manager?categoryIds=${ids.join(',')}`;;
}

/**
 * Quick helper: Create deep link for score range
 * 
 * @param scoreRange - Score range ID ('low', 'medium', 'high', etc.)
 * @returns URL path with scoreRange parameter
 * 
 * @example
 * <Button onClick={() => navigate(createScoreRangeDeepLink('high'))}>
 *   View High Score Profiles
 * </Button>
 */
export function createScoreRangeDeepLink(scoreRange: string): string {
  return `/profile-manager?scoreRange=${scoreRange}`;
}

/**
 * Quick helper: Create deep link for search query
 * 
 * @param searchTerm - Search term
 * @returns URL path with search parameter
 * 
 * @example
 * <Button onClick={() => navigate(createSearchDeepLink('Munich'))}>
 *   Search Munich
 * </Button>
 */
export function createSearchDeepLink(searchTerm: string): string {
  return `/profile-manager?search=${encodeURIComponent(searchTerm)}`;
}

/**
 * Validate if URL parameters are valid
 * Returns array of warnings for invalid params (empty = all valid)
 * 
 * @param searchParams - URLSearchParams to validate
 * @returns Array of warning messages
 * 
 * @example
 * const warnings = validateDeepLinkParams(searchParams);
 * if (warnings.length > 0) {
 *   console.warn('Invalid URL params:', warnings);
 * }
 */
export function validateDeepLinkParams(searchParams: URLSearchParams): string[] {
  const warnings: string[] = [];
  
  // Validate listId
  const listId = searchParams.get('listId');
  if (listId && isNaN(parseInt(listId))) {
    warnings.push(`Invalid listId: "${listId}" (must be a number)`);
  }
  
  // Validate categoryIds
  const categoryIds = searchParams.get('categoryIds');
  if (categoryIds) {
    const ids = categoryIds.split(',').map(id => parseInt(id.trim()));
    if (ids.some(id => isNaN(id))) {
      warnings.push(`Invalid categoryIds: "${categoryIds}" (must be comma-separated numbers)`);
    }
  }
  
  // Validate createdBy
  const createdBy = searchParams.get('createdBy');
  if (createdBy && isNaN(parseInt(createdBy))) {
    warnings.push(`Invalid createdBy: "${createdBy}" (must be a number)`);
  }
  
  // Validate date fields
  const afterDate = searchParams.get('afterDate');
  if (afterDate && isNaN(Date.parse(afterDate))) {
    warnings.push(`Invalid afterDate: "${afterDate}" (must be valid date)`);
  }
  
  const beforeDate = searchParams.get('beforeDate');
  if (beforeDate && isNaN(Date.parse(beforeDate))) {
    warnings.push(`Invalid beforeDate: "${beforeDate}" (must be valid date)`);
  }
  
  // Validate boolean fields
  const booleanFields = ['isExported', 'isActive', 'isBlocked', 'isRegistered', 'hasCategories', 'hasLists'];
  for (const field of booleanFields) {
    const value = searchParams.get(field);
    if (value && value !== 'true' && value !== 'false') {
      warnings.push(`Invalid ${field}: "${value}" (must be "true" or "false")`);
    }
  }
  
  return warnings;
}